'use client'

import { useEffect, useMemo } from 'react'
import { Identify, identify, setUserId } from '@amplitude/analytics-browser'
import { usePathname } from 'next/navigation'

import { useAuth } from '@/hooks/useAuth'
import useAmplitudeTracking from '@/hooks/useAmplitudeTracking'
import { useGtmPush } from '@/hooks/useGtmPush'
import { isPublicRoute } from '@/middleware'
import { UserGroup } from '@/types/user.type'

export default function AmplitudePageViewWithUserId() {
  const pathname = usePathname()
  const isPublicRoutePage = useMemo(() => isPublicRoute(pathname), [pathname])

  // Always call useAuth hook - never conditionally
  const authResult = useAuth()

  // Handle the public route logic after the hook call
  const user = isPublicRoutePage ? undefined : authResult?.user

  const { trackPageView } = useAmplitudeTracking(user)
  const { setGtmUserProperties } = useGtmPush()

  useEffect(() => {
    if (pathname) {
      trackPageView({ path: pathname })
    }
  }, [pathname, trackPageView])

  useEffect(() => {
    // Handle Amplitude user ID
    if (isPublicRoutePage || user === undefined) {
      setUserId(undefined)
      return
    }

    const userId = user ? (user.group === UserGroup.Internal ? 'internal' : user.id) : undefined
    setUserId(userId)
    const profile = new Identify().set('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone)
    // Every active profile gets the label here, not only the ones a server-side event reaches.
    if (user?.account_type) profile.set('account_type', user.account_type)
    identify(profile)

    // Only set GTM properties if not on public route and user exists
    if (!isPublicRoutePage && user) {
      setGtmUserProperties(user)
    }
  }, [user, isPublicRoutePage, setGtmUserProperties])

  return null
}
